import type { AppRole } from "./auth";
import type { Company } from "../types/database";
import { supabase } from "./supabase";

function unwrapOne<T>(rel: T | T[] | null | undefined): T | null {
  if (rel == null) return null;
  return Array.isArray(rel) ? (rel[0] ?? null) : rel;
}

/** Companies the signed-in user belongs to via `company_users`. */
export async function getMyCompanies(): Promise<Company[]> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) return [];

  const { data, error } = await supabase
    .from("company_users")
    .select(
      "companies (id, name, industry, size, website, logo_url, description, tagline, engagement_model)"
    )
    .eq("user_id", userData.user.id);

  if (error || !data) return [];

  const out: Company[] = [];
  for (const row of data as { companies: unknown }[]) {
    const c = unwrapOne(row.companies as Company | Company[] | null);
    if (c && typeof c === "object" && "id" in c && typeof c.id === "string") {
      out.push(c as Company);
    }
  }
  return out;
}

// ─── Company members (Your Company → Team tab) ─────────────────────────

export type CompanyOwnership = "owner" | "member";

export type CompanyMember = {
  user_id: string;
  full_name: string | null;
  email: string | null;
  avatar_url: string | null;
  initials: string;
  /** Membership role in `company_users`. */
  company_role: CompanyOwnership;
  /** Platform AppRole from `user_roles` (the most recent one). May be null. */
  app_role: AppRole | null;
  /** TODO: `auth.users.last_sign_in_at` is not exposed via the client SDK and
   *  we don't mirror it onto `user_profiles` yet. Returns null for now —
   *  surface as "Recently active" in the UI until we add a lightweight
   *  per-user heartbeat (e.g. an RPC that touches a `last_seen_at` row). */
  last_seen_at: string | null;
};

function makeInitials(s: string | null | undefined): string {
  if (!s?.trim()) return "??";
  const parts = s.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

/**
 * Lists every user in a company. RLS scopes the visible rows automatically.
 *
 * No `list_company_members(company_id)` RPC exists yet — TODO to add one
 * server-side once we want to expose `last_seen_at` and richer role data.
 * For now we hand-join `company_users` → `user_profiles` + `user_roles`
 * client-side.
 */
export async function listCompanyMembers(companyId: string): Promise<CompanyMember[]> {
  const { data: links, error } = await supabase
    .from("company_users")
    .select("user_id, company_role, created_at")
    .eq("company_id", companyId)
    .order("created_at", { ascending: true });

  if (error || !links?.length) return [];

  type LinkRow = { user_id: string; company_role: CompanyOwnership };
  const linkRows = links as LinkRow[];
  const userIds = linkRows.map((l) => l.user_id);

  const [{ data: profiles }, { data: roles }] = await Promise.all([
    supabase
      .from("user_profiles")
      .select("id, full_name, email, avatar_url")
      .in("id", userIds),
    supabase
      .from("user_roles")
      .select("user_id, role, created_at")
      .in("user_id", userIds)
      .order("created_at", { ascending: false }),
  ]);

  type ProfileRow = { id: string; full_name: string | null; email: string | null; avatar_url: string | null };
  const profileById = new Map<string, ProfileRow>();
  for (const p of (profiles ?? []) as ProfileRow[]) profileById.set(p.id, p);

  // Pick the most recent role per user (rows are already created_at desc).
  type RoleRow = { user_id: string; role: AppRole };
  const roleByUser = new Map<string, AppRole>();
  for (const r of (roles ?? []) as RoleRow[]) {
    if (!roleByUser.has(r.user_id)) roleByUser.set(r.user_id, r.role);
  }

  return linkRows.map((l) => {
    const p = profileById.get(l.user_id);
    return {
      user_id: l.user_id,
      full_name: p?.full_name ?? null,
      email: p?.email ?? null,
      avatar_url: p?.avatar_url ?? null,
      initials: makeInitials(p?.full_name ?? p?.email ?? null),
      company_role: l.company_role,
      app_role: roleByUser.get(l.user_id) ?? null,
      last_seen_at: null,
    };
  });
}

/** Re-fetches a single company row by id. Used after the Settings → Company
 *  Appearance edit modal saves so the parent can render fresh values. */
export async function getCompanyById(companyId: string): Promise<Company | null> {
  const { data, error } = await supabase
    .from("companies")
    .select(
      "id, name, industry, size, website, logo_url, description, tagline, engagement_model"
    )
    .eq("id", companyId)
    .maybeSingle();
  if (error || !data) return null;
  return data as Company;
}

export type CompanyUpdate = {
  name?: string;
  website?: string;
  tagline?: string;
  description?: string;
};

/** Updates editable company fields. Empty / whitespace-only strings are
 *  stored as NULL. RLS on `companies` gates writes to company_users members. */
export async function updateCompany(
  companyId: string,
  patch: CompanyUpdate
): Promise<{ error: Error | null }> {
  const trimmed: Record<string, string | null> = {};
  for (const [k, v] of Object.entries(patch)) {
    if (typeof v === "string") trimmed[k] = v.trim() || null;
  }
  if (Object.keys(trimmed).length === 0) return { error: null };
  const { error } = await supabase
    .from("companies")
    .update(trimmed)
    .eq("id", companyId);
  return { error: error ? new Error(error.message) : null };
}

/** Returns a Map of user_id -> recruiting_team_id for every member of the
 *  given company. Empty values mean "no region assigned". Used by the
 *  Members tab to render the current region selection per row. */
export async function getCompanyMemberRegions(
  companyId: string,
): Promise<Map<string, string | null>> {
  const out = new Map<string, string | null>();
  const { data: cu } = await supabase
    .from("company_users")
    .select("user_id")
    .eq("company_id", companyId);
  for (const row of (cu ?? []) as { user_id: string }[]) out.set(row.user_id, null);

  const { data: teams } = await supabase
    .from("recruiting_teams")
    .select("id")
    .eq("company_id", companyId);
  const teamIds = ((teams ?? []) as { id: string }[]).map((t) => t.id);
  if (teamIds.length === 0) return out;

  const { data: assignments } = await supabase
    .from("recruiting_team_managers")
    .select("user_id, team_id")
    .in("team_id", teamIds);
  for (const a of (assignments ?? []) as { user_id: string; team_id: string }[]) {
    out.set(a.user_id, a.team_id);
  }
  return out;
}

// ─── Member role management — Members tab inline editing ─────────────

/**
 * Sets a teammate's app-level role for a company. Coravu stores roles in
 * `user_roles` (one row per role per user). For members, only one of
 * `client`, `manager`, `judge` should be active at a time, so this function
 * deletes any of those three that are NOT the target role, then upserts
 * the target. Doesn't touch admin / student rows the user may also hold.
 */
export async function setMemberRole(
  userId: string,
  role: "client" | "manager" | "judge",
): Promise<{ error: Error | null }> {
  const COMPANY_ROLES = ["client", "manager", "judge"] as const;
  const otherRoles = COMPANY_ROLES.filter((r) => r !== role);
  const { error: delErr } = await supabase
    .from("user_roles")
    .delete()
    .eq("user_id", userId)
    .in("role", otherRoles);
  if (delErr) return { error: new Error(delErr.message) };

  const { error: upErr } = await supabase
    .from("user_roles")
    .upsert({ user_id: userId, role }, { onConflict: "user_id,role" });
  return { error: upErr ? new Error(upErr.message) : null };
}

/** Removes a user from a company. Cleans up company_users + any region
 *  assignments. Their auth account stays alive but they lose access. */
export async function removeMemberFromCompany(
  userId: string,
  companyId: string,
): Promise<{ error: Error | null }> {
  const { data: teams } = await supabase
    .from("recruiting_teams")
    .select("id")
    .eq("company_id", companyId);
  const teamIds = ((teams ?? []) as { id: string }[]).map((t) => t.id);
  if (teamIds.length > 0) {
    const { error: rtErr } = await supabase
      .from("recruiting_team_managers")
      .delete()
      .eq("user_id", userId)
      .in("team_id", teamIds);
    if (rtErr) return { error: new Error(rtErr.message) };
  }
  const { error: cuErr } = await supabase
    .from("company_users")
    .delete()
    .eq("user_id", userId)
    .eq("company_id", companyId);
  return { error: cuErr ? new Error(cuErr.message) : null };
}

/** Returns a per-AppRole tally for a company. Roles with zero members are 0. */
export async function countCompanyMembersByRole(
  companyId: string
): Promise<Partial<Record<AppRole, number>>> {
  const members = await listCompanyMembers(companyId);
  const out: Partial<Record<AppRole, number>> = {};
  for (const m of members) {
    if (!m.app_role) continue;
    out[m.app_role] = (out[m.app_role] ?? 0) + 1;
  }
  return out;
}
